// Add / edit dialog for a team membership (see `features/teams-sports.md`).
// Adding picks an athlete of the company who holds a sport profile for the
// team's sport and is not already a member — the server resolves the profile
// and enforces the same rules. Editing changes only the two team-scoped
// fields, `tier` and `teamRole` (the linked athlete never changes). When the
// sport's config declares positions, they are offered as role suggestions,
// but the role stays free text — it may differ from any natural position.

import { useEffect, useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import type { Athlete, Membership, Sport, SportProfile, Team } from '../domain';
import { MEMBERSHIP_FIELD_MAX, validateMembershipFields } from '../domain';
import type { TeamsSnapshot } from '../data/apiClient';
import { addTeamMember, updateMembership } from '../data/apiClient';
import { useTranslation } from '../state/I18nContext';

interface MembershipDialogProps {
  open: boolean;
  /** The team the membership belongs to. */
  team: Team;
  /** The membership being edited, or null to add a new member. */
  membership: Membership | null;
  /** The company's athletes (for names and the eligible-athlete choice). */
  athletes: Athlete[];
  /** The company's sport profiles (who is eligible; membership -> athlete). */
  profiles: SportProfile[];
  /** The company's live memberships (to exclude current members when adding). */
  memberships: Membership[];
  /** The team's sport definition, for role suggestions ('' label fallback). */
  sport: Sport | undefined;
  onClose: () => void;
  onSaved: (snapshot: TeamsSnapshot) => void;
}

export function MembershipDialog({
  open,
  team,
  membership,
  athletes,
  profiles,
  memberships,
  sport,
  onClose,
  onSaved,
}: MembershipDialogProps) {
  const { t } = useTranslation();

  const [athleteId, setAthleteId] = useState('');
  const [tier, setTier] = useState('');
  const [teamRole, setTeamRole] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const byProfileId = useMemo(
    () => new Map(profiles.map((profile) => [profile.id, profile])),
    [profiles],
  );
  const athleteByNumber = useMemo(
    () => new Map(athletes.map((athlete) => [athlete.personalNumber, athlete])),
    [athletes],
  );

  // The athlete a membership under edit links to (via its sport profile).
  const editedAthlete = membership
    ? athleteByNumber.get(byProfileId.get(membership.sportProfileId)?.athleteId ?? '')
    : undefined;

  // (Re)seed the form whenever the dialog opens for a different target.
  useEffect(() => {
    if (!open) return;
    setAthleteId('');
    setTier(membership?.tier ?? '');
    setTeamRole(membership?.teamRole ?? '');
    setError(null);
  }, [open, membership]);

  // Who may join: athletes of the company holding a profile for the team's
  // sport, minus those already in this team. The server enforces the same.
  const eligible = useMemo(() => {
    const memberProfileIds = new Set(
      memberships.filter((m) => m.teamId === team.id).map((m) => m.sportProfileId),
    );
    return profiles
      .filter((p) => p.sportId === team.sportId && !memberProfileIds.has(p.id))
      .map((p) => athleteByNumber.get(p.athleteId))
      .filter((athlete): athlete is Athlete => athlete !== undefined);
  }, [profiles, memberships, team, athleteByNumber]);

  // Position values from the sport's config double as role suggestions.
  const roleSuggestions = useMemo(() => {
    const spec = sport?.config.attributes.position;
    return spec && spec.kind === 'enum' ? spec.values : [];
  }, [sport]);

  const fieldsValidation = useMemo(
    () => validateMembershipFields({ tier, teamRole }),
    [tier, teamRole],
  );
  const valid = fieldsValidation.valid && (membership !== null || athleteId.length > 0);

  const sportName = (() => {
    const translated = t(`sports.${team.sportId}`);
    if (translated !== `sports.${team.sportId}`) return translated;
    return sport?.name ?? team.sportId;
  })();

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const snapshot = membership
        ? await updateMembership(membership.id, { tier, teamRole })
        : await addTeamMember(team.id, { athleteId, tier, teamRole });
      onSaved(snapshot);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.teams.errors.member'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {membership
          ? t('admin.teams.memberDialog.editTitle', {
              name: editedAthlete?.fullName ?? '',
            })
          : t('admin.teams.memberDialog.addTitle', { team: team.name })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {membership ? (
            <Typography variant="body2" color="text.secondary">
              {editedAthlete?.fullName}
            </Typography>
          ) : eligible.length === 0 ? (
            <Alert severity="info">
              {t('admin.teams.memberDialog.noEligible', { sport: sportName })}
            </Alert>
          ) : (
            <TextField
              select
              label={t('admin.teams.memberDialog.athlete')}
              value={athleteId}
              onChange={(e) => setAthleteId(e.target.value)}
              helperText={t('admin.teams.memberDialog.athleteHelp', {
                sport: sportName,
              })}
            >
              {eligible.map((athlete) => (
                <MenuItem key={athlete.personalNumber} value={athlete.personalNumber}>
                  {athlete.fullName} ({athlete.personalNumber})
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            label={t('admin.teams.memberDialog.tier')}
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            helperText={t('admin.teams.memberDialog.tierHelp')}
            slotProps={{ htmlInput: { maxLength: MEMBERSHIP_FIELD_MAX } }}
          />
          <Autocomplete
            freeSolo
            options={roleSuggestions}
            inputValue={teamRole}
            onInputChange={(_, value) => setTeamRole(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('admin.teams.memberDialog.teamRole')}
                helperText={t('admin.teams.memberDialog.roleHelp')}
                slotProps={{
                  htmlInput: { ...params.inputProps, maxLength: MEMBERSHIP_FIELD_MAX },
                }}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          {t('admin.common.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={busy || !valid}
          onClick={() => void submit()}
        >
          {t('admin.common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
